import fastaToJson from "./fastaToJson";
import genbankToJson from "./genbankToJson";
import sbolXmlToJson from "./sbolXmlToJson";
import extractFileExtension from "./utils/extractFileExtension.js";
import snapgeneToJson from "./snapgeneToJson";
import ab1ToJson from "./ab1ToJson";
import gffToJson from "./gffToJson";
import isBrowser from "./utils/isBrowser";
import { tidyUpSequenceData } from "@teselagen/sequence-utils";
import geneiousXmlToJson from "./geneiousXmlToJson";
import jbeiXmlToJson from "./jbeiXmlToJson";
import { unzipSync } from "fflate";
import fastqToJson from "./fastqToJson";
async function anyToJson(fileContentStringOrFileObj, options) {
  let fileContentString;
  options = options || {};
  let fileName = options.fileName || "";
  if (!fileName && typeof fileContentStringOrFileObj !== "string") {
    fileName = fileContentStringOrFileObj.name;
    options.fileName = fileName;
  }
  const ext = extractFileExtension(fileName);
  if (typeof fileContentStringOrFileObj === "string") {
    fileContentString = fileContentStringOrFileObj;
  } else {
    if (/^(ab1)$/.test(ext)) {
      return ab1ToJson(fileContentStringOrFileObj, options);
    } else if (/^(prot)$/.test(ext)) {
      return snapgeneToJson(fileContentStringOrFileObj, options);
    } else if (/^(dna)$/.test(ext)) {
      return snapgeneToJson(fileContentStringOrFileObj, options);
    } else if (/^(geneious)$/.test(ext)) {
      const a = await getUint8ArrayFromFile(fileContentStringOrFileObj);
      let d;
      try {
        d = new TextDecoder().decode(a, { stream: false });
        if (!d.includes("<geneious")) {
          throw new Error("not geneious");
        }
      } catch (e) {
        const b = unzipSync(a);
        const c = Object.values(b)[0];
        d = new TextDecoder().decode(c, { stream: false });
      }
      return geneiousXmlToJson(d, options);
    } else {
      fileContentString = await getUtf8StringFromFile(
        fileContentStringOrFileObj,
        options
      );
    }
  }
  if (/^(fasta|fas|fa|fna|ffn|faa)$/.test(ext)) {
    return fastaToJson(fileContentString, options);
  } else if (/^(fastq)$/.test(ext)) {
    return fastqToJson(fileContentString, options);
  } else if (/^(gb|gbk)$/.test(ext)) {
    return genbankToJson(fileContentString, options);
  } else if (/^(seq)$/.test(ext) || /^(xml)$/.test(ext) && fileContentString.includes("seq:seq") && fileContentString.includes("jbei")) {
    return jbeiXmlToJson(fileContentString, options);
  } else if (/^(json)$/.test(ext)) {
    const failure = {
      messages: [`Unable to parse JSON file ${fileName}`],
      success: false
    };
    try {
      const cleaned = tidyUpSequenceData(
        JSON.parse(fileContentString),
        options
      );
      if (!cleaned.sequence.length) return [failure];
      return [{ parsedSequence: cleaned, success: true }];
    } catch (error) {
      console.error(`error:`, error);
      return [failure];
    }
  } else if (/^(gp|genpep)$/.test(ext)) {
    return genbankToJson(fileContentString, { ...options, isProtein: true });
  } else if (/^(xml|rdf)$/.test(ext)) {
    return sbolXmlToJson(
      fileContentString || fileContentStringOrFileObj,
      options
    );
  } else if (/^(gff|gff3)$/.test(ext)) {
    return gffToJson(fileContentStringOrFileObj, options);
  } else {
    let parsersToTry = [
      {
        fn: genbankToJson,
        name: "Genbank Parser"
      },
      {
        fn: fastaToJson,
        name: "Fasta Parser"
      }
    ];
    const firstChar = fileContentString[fileContentString.search(/\S|$/)];
    if (firstChar === ">") {
      parsersToTry = parsersToTry.sort((a) => {
        if (a.name === "Fasta Parser") return -1;
        return 1;
      });
    } else if (firstChar === "L") {
      parsersToTry = parsersToTry.sort((a) => {
        if (a.name === "Genbank Parser") return -1;
        return 1;
      });
    }
    for (const parser of parsersToTry) {
      const toReturn = await parser.fn(fileContentString, options);
      if (successfulParsing(toReturn)) {
        toReturn.forEach(function(result) {
          result.messages.push("Parsed using " + parser.name + ".");
        });
        return toReturn;
      }
    }
    return [
      {
        messages: [
          "Unable to parse file as FASTA, genbank, JBEI, or SBOL formats"
        ],
        success: false
      }
    ];
  }
  function successfulParsing(resultArray) {
    return resultArray.some(function(result) {
      return result.success;
    });
  }
}
export default anyToJson;
function getUtf8StringFromFile(file, { emulateBrowser } = {}) {
  if (!isBrowser && !emulateBrowser) {
    return Buffer.isBuffer(file) ? file.toString("utf-8") : Buffer.isBuffer(file.buffer) ? file.buffer.toString("utf-8") : file;
  }
  const reader = new window.FileReader();
  reader.readAsText(file, "UTF-8");
  return new Promise((resolve, reject) => {
    reader.onload = (evt) => {
      resolve(evt.target.result);
    };
    reader.onerror = (err) => {
      console.error("err:", err);
      reject(err);
    };
  });
}
function getUint8ArrayFromFile(file, { emulateBrowser } = {}) {
  if (!isBrowser && !emulateBrowser) {
    return Buffer.isBuffer(file) ? new Uint8Array(file) : Buffer.isBuffer(file.buffer) ? new Uint8Array(file.buffer) : file;
  }
  const reader = new window.FileReader();
  reader.readAsArrayBuffer(file);
  return new Promise((resolve, reject) => {
    reader.onload = (evt) => {
      const arrayBuffer = evt.target.result;
      const bytes = new Uint8Array(arrayBuffer);
      resolve(bytes);
    };
    reader.onerror = (err) => {
      console.error("err:", err);
      reject(err);
    };
  });
}
